/**
 * How the Trial Master File checklist is grouped, counted and labelled.
 *
 * ONE VOCABULARY, READ BY BOTH CHECKLIST PAGES. The Documents & TMF step
 * (`pages/project/Documents.tsx`) and the Essential Documents page
 * (`pages/EssentialDocs.tsx`) render the same `projects/{id}/essentialDocs`
 * collection. Until 2026-08-25 each carried its own private copy of the zone
 * names and the status vocabulary, and one of the two copies was wrong in every
 * particular: it read `documentName` where the server writes `name`, and counted
 * `status === "completed"` against a server vocabulary of
 * `required | in_progress | complete | not_applicable`, which has no `completed`
 * in it. Every row rendered blank, every count read zero, and the subsystem took
 * no traffic for thirty days. `functions/src/generateAllFromProtocol.ts` names
 * that exact mismatch in its header comment as the reason a block of seeding was
 * deleted on 2026-08-02, so it was known and unfixed for three weeks.
 *
 * The naming, the vocabulary and the grouping now live here, once, and both
 * pages read them. That is the fix for the drift as well as for the defect: two
 * private copies of one vocabulary is how the two pages came apart.
 *
 * Pure. No React, no DOM, no Firebase — so it is testable from the `functions`
 * package's runner (`cd functions && npm run test:web`), which is how the
 * frontend's logic gets tested at all: `web` has no test runner and must not
 * gain one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS A DELIBERATE PORT, AND THESE COPIES MUST STAY IN STEP:
 *
 *   • the zone list       — `TMF_ZONES` in `functions/src/data/essentialDocs.ts`
 *   • the status set      — `validStatuses` in `updateEssentialDocStatusHandler`
 *
 * The web bundle does not compile the `functions/` tree, so the lists have to be
 * copied rather than imported. `tmfChecklistView.test.ts` imports the server's
 * `TMF_ZONES` and asserts this file's copy matches it id for id, name for name
 * and description for description, in order — a drifted copy fails CI. Same
 * pattern, and the same reasoning, as `tmfAttachments.ts` and
 * `scheduleConfirmationStatus.ts`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * The four statuses `updateEssentialDocStatusV2` accepts and
 * `getEssentialDocsV2` counts. Note what is NOT here: `completed`, `not_started`
 * and `pending` have all appeared in frontend code at some point and none has
 * ever been written by anything.
 */
export const TMF_CHECKLIST_STATUSES = [
  'required',
  'in_progress',
  'complete',
  'not_applicable',
] as const;

export type TmfChecklistStatus = typeof TMF_CHECKLIST_STATUSES[number];

/**
 * A checklist entry as it comes out of Firestore.
 *
 * Every field is optional because the collection genuinely holds two shapes: the
 * full template `initializeTMFHandler` writes, and the six sparse stub rows the
 * pre-2026-08-02 `generateAllFromProtocolV2` epilogue left behind on older
 * studies, which carry only `name`, `zone`, `category` and `status`.
 */
export interface TmfChecklistEntryView {
  id?: string | null;
  templateId?: string | null;
  name?: string | null;
  description?: string | null;
  category?: string | null;
  zone?: string | null;
  locatedIn?: string | null;
  status?: string | null;
}

/**
 * The Trial Master File zones, ported from `TMF_ZONES`.
 *
 * The `sections` each zone carries server-side are omitted — nothing renders
 * them, and copying a list nothing reads is a copy that drifts for free.
 */
export const TMF_ZONES_VIEW: ReadonlyArray<{
  id: string;
  name: string;
  description: string;
}> = [
  {
    id: 'zone1_trial_management',
    name: 'Trial Management',
    description: 'Trial oversight, planning, and management documents',
  },
  {
    id: 'zone2_central_trial_docs',
    name: 'Central Trial Documents',
    description: 'Core trial documents including protocol and IB',
  },
  {
    id: 'zone3_irb_ethics',
    name: 'IRB/Ethics',
    description: 'IRB/IEC submissions and approvals',
  },
  {
    id: 'zone4_regulatory',
    name: 'Regulatory',
    description: 'Regulatory submissions and approvals',
  },
  {
    id: 'zone5_site_management',
    name: 'Site Management',
    description: 'Site-level documentation',
  },
  {
    id: 'zone6_ip_management',
    name: 'IP Management',
    description: 'Investigational product management',
  },
  {
    id: 'zone7_safety',
    name: 'Safety Reporting',
    description: 'Safety data and reporting',
  },
  {
    id: 'zone8_central_lab',
    name: 'Central Facilities/Monitoring',
    description: 'Central lab, monitoring, and vendor management',
  },
  {
    id: 'zone9_statistics',
    name: 'Statistics',
    description: 'Statistical analysis and reporting',
  },
  {
    id: 'zone10_data_management',
    name: 'Data Management',
    description: 'Data collection and management',
  },
];

/**
 * Where an entry goes when its `zone` is not one this build knows.
 *
 * NOT a cosmetic fallback — it is the reason nothing disappears. The stub rows
 * the old pipeline wrote carry short zone ids (`zone1`, `zone5`) from a taxonomy
 * that was replaced, and in that taxonomy `zone5` meant Data Management while
 * the current `zone5_site_management` means Site Management. Prefix-matching the
 * two would file a Data Management Plan under Site Management and look right
 * while doing it, so an unrecognized zone is held aside and said out loud
 * instead of guessed at.
 */
export const UNASSIGNED_ZONE_ID = '__unassigned__';
export const UNASSIGNED_ZONE_NAME = 'Unassigned';
export const UNASSIGNED_ZONE_DESCRIPTION =
  'Entries whose Trial Master File zone is not one this checklist recognizes. ' +
  'Set the study\'s Trial Master File up again to file them.';

function present(value: unknown): string {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : '';
}

/** The zone's display name, the raw id when it is not a zone we know, `"Unassigned"` when there is none. */
export function zoneName(zoneId: string | null | undefined): string {
  const id = present(zoneId);
  if (!id) return UNASSIGNED_ZONE_NAME;
  return TMF_ZONES_VIEW.find(z => z.id === id)?.name ?? id;
}

/**
 * What the row calls this entry.
 *
 * `name` is the only field any writer has ever produced. A row with nothing
 * readable says so rather than rendering an empty line, because a blank row is
 * indistinguishable from the defect this module exists to fix.
 */
export function entryName(entry: TmfChecklistEntryView | null | undefined): string {
  return present(entry?.name) || 'Unnamed checklist entry';
}

export function isTmfChecklistStatus(value: unknown): value is TmfChecklistStatus {
  return typeof value === 'string'
    && (TMF_CHECKLIST_STATUSES as readonly string[]).includes(value);
}

const STATUS_LABELS: Record<TmfChecklistStatus, string> = {
  required: 'Not Started',
  in_progress: 'In Progress',
  complete: 'Complete',
  not_applicable: 'N/A',
};

/**
 * The status in words.
 *
 * An unrecognized status is reported AS unrecognized. The shipped page had a
 * `default:` arm returning "Not Started", which is precisely what let every
 * `complete` row render red and not-started for three weeks: a wrong answer and
 * a plausible one are the same pixel.
 */
export function statusLabel(status: string | null | undefined): string {
  return isTmfChecklistStatus(status) ? STATUS_LABELS[status] : 'Unrecognized';
}

const STATUS_BADGE_CLASSES: Record<TmfChecklistStatus, string> = {
  required: 'bg-red-100 text-red-700',
  in_progress: 'bg-amber-100 text-amber-700',
  complete: 'bg-green-100 text-green-700',
  not_applicable: 'bg-gray-100 text-gray-500',
};

/** Tailwind classes for the status pill. Unrecognized gets its own, not a known one. */
export function statusBadgeClasses(status: string | null | undefined): string {
  return isTmfChecklistStatus(status)
    ? STATUS_BADGE_CLASSES[status]
    : 'bg-slate-100 text-slate-600';
}

/**
 * Who holds this document, spelled out.
 *
 * Returns `''` rather than falling through to "Site" for a value the record does
 * not carry — a legacy stub row has no `locatedIn` at all, and printing a
 * location it never claimed is inventing a fact.
 */
export function locatedInLabel(locatedIn: string | null | undefined): string {
  switch (present(locatedIn)) {
    case 'both': return 'Sponsor & Site';
    case 'sponsor': return 'Sponsor';
    case 'site': return 'Site';
    default: return '';
  }
}

export interface TmfChecklistSummary {
  total: number;
  complete: number;
  inProgress: number;
  notStarted: number;
  notApplicable: number;
  /** Entries whose status is none of the four. Counted, never folded into notStarted. */
  unrecognized: number;
  /** `complete / total` as a whole percent. `0` for an empty checklist. */
  completionPercent: number;
}

/** The counts the stat tiles and the progress bars read. Mirrors `getEssentialDocsHandler`'s stats block. */
export function summarizeChecklist(
  entries: ReadonlyArray<TmfChecklistEntryView> | null | undefined,
): TmfChecklistSummary {
  const all = (entries ?? []).filter(Boolean);
  const count = (status: TmfChecklistStatus) =>
    all.filter(e => e.status === status).length;

  const complete = count('complete');
  const total = all.length;

  return {
    total,
    complete,
    inProgress: count('in_progress'),
    notStarted: count('required'),
    notApplicable: count('not_applicable'),
    unrecognized: all.filter(e => !isTmfChecklistStatus(e.status)).length,
    completionPercent: total > 0 ? Math.round((complete / total) * 100) : 0,
  };
}

export interface TmfZoneGroup<T extends TmfChecklistEntryView = TmfChecklistEntryView> {
  id: string;
  name: string;
  description: string;
  entries: T[];
  summary: TmfChecklistSummary;
}

/**
 * The checklist filed into its zones.
 *
 * Every zone is returned whether or not this study has anything in it, because
 * an empty zone is information — it is a part of the trial folder nobody has
 * filled in. The holding group is appended only when something landed in it.
 *
 * The invariant the tests hold this to: every entry handed in comes back out,
 * exactly once. A grouping that silently drops rows is the defect this replaced.
 */
export function groupEntriesByZone<T extends TmfChecklistEntryView>(
  entries: ReadonlyArray<T> | null | undefined,
): TmfZoneGroup<T>[] {
  const all = (entries ?? []).filter(Boolean);
  const known = new Set(TMF_ZONES_VIEW.map(z => z.id));

  const groups: TmfZoneGroup<T>[] = TMF_ZONES_VIEW.map(zone => {
    const inZone = all.filter(e => present(e.zone) === zone.id);
    return {
      id: zone.id,
      name: zone.name,
      description: zone.description,
      entries: inZone,
      summary: summarizeChecklist(inZone),
    };
  });

  const unassigned = all.filter(e => !known.has(present(e.zone)));
  if (unassigned.length > 0) {
    groups.push({
      id: UNASSIGNED_ZONE_ID,
      name: UNASSIGNED_ZONE_NAME,
      description: UNASSIGNED_ZONE_DESCRIPTION,
      entries: unassigned,
      summary: summarizeChecklist(unassigned),
    });
  }

  return groups;
}

/**
 * The zone list narrowed to a search.
 *
 * A zone whose own name or description matches keeps all of its entries — you
 * asked for the zone. Otherwise the zone survives only if a document in it
 * matches, narrowed to the documents that did, with its counts recomputed so the
 * header describes what is actually on screen.
 */
export function filterZoneGroups<T extends TmfChecklistEntryView>(
  groups: ReadonlyArray<TmfZoneGroup<T>>,
  query: string | null | undefined,
): TmfZoneGroup<T>[] {
  const needle = present(query).toLowerCase();
  if (!needle) return [...groups];

  const hit = (value: string | null | undefined) =>
    present(value).toLowerCase().includes(needle);

  const found: TmfZoneGroup<T>[] = [];
  for (const group of groups) {
    if (hit(group.name) || hit(group.description)) {
      found.push(group);
      continue;
    }
    const matches = group.entries.filter(e => hit(e.name) || hit(e.description));
    if (matches.length > 0) {
      found.push({ ...group, entries: matches, summary: summarizeChecklist(matches) });
    }
  }
  return found;
}
